// Wire contract — CMDB projection reads (the register's machine mirror).
// Contract home: DATA-CONTRACTS §CMDB projections; concept: ems-domain ADR-009 /
// DOMAIN-RULES §6. Machine consumers only (ApiKey actor holding
// cmdb_projection:read) — never a frontend surface.
//
// Shape law: full snapshots, `{ version, items }`, versioned by ETag — no
// pagination ever (a mirror assembled from pages can tear). Field admission is
// per NAMED consumer (the projections' own admission law, stricter than the
// general L7 rule): every field below names who rebuilds from it in
// DATA-CONTRACTS' per-read tables. Nothing reach-shaped rides any projection —
// addresses, ports, credentials live behind the device-targets read.
import { z } from 'zod';
import { ProjectStatusSchema } from '../projects/platform-responses.js';
import { AssetStatusSchema } from '../assets/requests.js';
import { AssetCategorySchema } from '../assets/categories.js';
import { SITE_PROJECTION_STATUSES } from '../sites/requests.js';
// The envelope: version = the projection's counter at the same snapshot as the
// rows (a body always matches its ETag). Monotonic, never dense — consumers
// compare by inequality.
function projectionEnvelope(item) {
    return z.object({
        version: z.number().int().nonnegative(),
        items: z.array(item),
    });
}
// ── projects — GET /api/platform/projections/projects (fleet keys only) ─────
export const ProjectProjectionItemSchema = z.object({
    id: z.string(), // the stable key — enters stamps, tags, partitions
    name: z.string(),
    status: ProjectStatusSchema, // terminated rows keep serving (history keeps its dimension)
});
export const ProjectsProjectionSchema = projectionEnvelope(ProjectProjectionItemSchema);
// ── models — GET /api/platform/projections/models (fleet keys only) ─────────
export const ModelProjectionItemSchema = z.object({
    id: z.string(), // the FK anchor assets reference
    canonical_key: z.string().nullable(), // catalog-bundle binding; NULL local = "not yet monitorable"
    manufacturer: z.string().nullable(), // NULL on generic rows (CHECK-enforced shape)
    name: z.string(),
    series: z.string().nullable(), // dims human grouping (admitted 2026-08-18; consumer: the dims builder)
    category: AssetCategorySchema, // the grouping axis (population rollups)
    is_network_device: z.boolean().nullable(), // NULL = inherits the category default
    is_canonical: z.boolean(),
    is_generic: z.boolean(),
    is_disabled: z.boolean(), // a merge's absorbed loser — already re-pointed, kept for the mirror
});
export const ModelsProjectionSchema = projectionEnvelope(ModelProjectionItemSchema);
// ── sites — GET /api/projects/:projectId/projections/sites ──────────────────
// Population: the PROJECTION family (sites/requests — activation onward, never
// drafts: the permanence invariant), so every activation-required column
// serves non-null.
export const SiteProjectionStatusSchema = z.enum(SITE_PROJECTION_STATUSES);
export const SiteProjectionItemSchema = z.object({
    id: z.string(), // the stamp dimension
    name: z.string(),
    reference: z.string(), // the human site key — id-primary, reference-as-label in dims
    status: SiteProjectionStatusSchema,
    timezone_iana: z.string(), // load-bearing: daily KPI buckets run on site-local days
    latitude: z.number(), // admitted 2026-08-18; consumers: dims builder / NOC map
    longitude: z.number(), // admitted 2026-08-18; consumers: dims builder / NOC map
    country_iso_code: z.string(), // admitted 2026-08-18; consumer: country rollups
    zone_id: z.number().int(), // the grouping tree, denormalized
    zone_name: z.string(),
    cluster_id: z.number().int(),
    cluster_name: z.string(),
    site_type_id: z.number().int(), // as-now classification (history stays app-side — the designation SCD)
    site_type_name: z.string(),
    site_priority_id: z.number().int(), // feeds coverage grading
    site_priority_name: z.string(),
});
export const SitesProjectionSchema = projectionEnvelope(SiteProjectionItemSchema);
// ── assets — GET /api/projects/:projectId/projections/assets ────────────────
// The register-edges input to subject binding. All five statuses serve;
// unallocated units (NULL project) appear in no project's read.
export const AssetProjectionItemSchema = z.object({
    id: z.string(), // subject identity
    site_id: z.string().nullable(), // placement (absent from the sites read = pre-activation → park)
    warehouse_id: z.string().nullable(),
    status: AssetStatusSchema,
    reference: z.string(), // the register's own key (NOT NULL)
    name: z.string().nullable(),
    serial_number: z.string().nullable(), // evidence, never identity — duplicates legal
    asset_model_id: z.string().nullable(), // NULL = not yet identified (the register's completeness loop) — unbindable until curated
    canonical_key: z.string().nullable(), // model binding, denormalized; NULL follows a null model
    category: AssetCategorySchema,
    parent_asset_id: z.string().nullable(), // containment edge
    accessed_via_asset_id: z.string().nullable(), // reach-through edge
    unit_count: z.number().int().nullable(), // aggregates only — population math is count × unit facts
});
export const AssetsProjectionSchema = projectionEnvelope(AssetProjectionItemSchema);
// ── devices — GET /api/projects/:projectId/projections/devices ──────────────
// The reporter roster: the device-ROLE view of assets (network-capable per the
// model flag ?? category default). Same population law as assets. The reserved
// `wiring` field is deliberately absent until the register's wiring-map facet
// lands — consumers must tolerate absence (arrival is additive).
export const DeviceProjectionItemSchema = z.object({
    id: z.string(),
    site_id: z.string().nullable(),
    warehouse_id: z.string().nullable(),
    status: AssetStatusSchema,
    reference: z.string(),
    name: z.string().nullable(),
    serial_number: z.string().nullable(),
    asset_model_id: z.string().nullable(), // which dictionary translates this reporter — the config compiler's binding path; NULL = not yet identified
    canonical_key: z.string().nullable(),
    accessed_via_asset_id: z.string().nullable(), // the fronting edge (NOC debug)
});
export const DevicesProjectionSchema = projectionEnvelope(DeviceProjectionItemSchema);
